import { Dispatch, SetStateAction, useCallback } from 'react';
import { RemovePIIReportEvents } from 'src/components/pages/Case/Case.slice';
import { DataDeletionType, TextRange } from 'src/graphql/generated';
import { FieldWithTextRange } from '../TextDeletionInput/TextDeletionInput';

interface UseTextRangeDeleteRequestedProps {
  setPayload: Dispatch<SetStateAction<DataDeletionType>>;
  reportEvents: RemovePIIReportEvents[];
  findEventDeletionIndex: (
    eventDeletions: Array<{ eventId: string }>,
    eventId: string,
  ) => number;
}

export function useTextRangeDeleteRequested({
  setPayload,
  reportEvents,
  findEventDeletionIndex,
}: UseTextRangeDeleteRequestedProps) {
  return useCallback(
    (range: TextRange, field: FieldWithTextRange, eventId?: string) => {
      setPayload((prevPayload: DataDeletionType) => {
        if (eventId && reportEvents && reportEvents.length >= 1) {
          // Event-scoped mode: update eventDeletions array
          const existingEventDeletions = prevPayload.eventDeletions || [];
          const eventIndex = findEventDeletionIndex(
            existingEventDeletions,
            eventId,
          );
          if (eventIndex >= 0) {
            // Update existing event deletion
            const updatedEventDeletions = [...existingEventDeletions];
            const currentEvent = updatedEventDeletions[eventIndex];

            // Only update fields that exist in EventDeletionType
            if (field === 'description') {
              updatedEventDeletions[eventIndex] = {
                ...currentEvent,
                description: [...currentEvent.description, range],
              };
            } else if (field === 'emotion') {
              updatedEventDeletions[eventIndex] = {
                ...currentEvent,
                emotion: [...currentEvent.emotion, range],
              };
            } else if (field === 'witnesses') {
              updatedEventDeletions[eventIndex] = {
                ...currentEvent,
                witnesses: [...currentEvent.witnesses, range],
              };
            } else if (field === 'location') {
              updatedEventDeletions[eventIndex] = {
                ...currentEvent,
                location: [...currentEvent.location, range],
              };
            } else if (field === 'externalNamedOffenders') {
              updatedEventDeletions[eventIndex] = {
                ...currentEvent,
                externalNamedOffenders: [
                  ...(currentEvent.externalNamedOffenders || []),
                  range,
                ],
              };
            }

            return {
              ...prevPayload,
              eventDeletions: updatedEventDeletions,
            };
          } else {
            // Create new event deletion
            const newEventDeletion = {
              eventId,
              description: field === 'description' ? [range] : [],
              emotion: field === 'emotion' ? [range] : [],
              witnesses: field === 'witnesses' ? [range] : [],
              location: field === 'location' ? [range] : [],
              externalNamedOffenders:
                field === 'externalNamedOffenders' ? [range] : undefined,
              preSubmissionS3Paths: [],
            };
            return {
              ...prevPayload,
              eventDeletions: [...existingEventDeletions, newEventDeletion],
            };
          }
        } else {
          // Legacy mode: update report-wide fields
          return {
            ...prevPayload,
            [field]: [...prevPayload[field], range],
          };
        }
      });
    },
    [setPayload, reportEvents, findEventDeletionIndex],
  );
}
